
    import {Inject, Options, Vue} from 'vue-property-decorator';
    import {Tag} from '@/profile-creation-wizard/components/for-creation/Tag';
    import {DI} from '@/profile-creation-wizard/common/DependencyInjection';
    import {FreelancerTagsService} from '@/profile-creation-wizard/components/for-creation/FreelancerTagsService';
    import SelectedTags from '@/profile-creation-wizard/components/for-creation/ui/SelectedTags.vue';
    import InputSelectTags from '../../InputSelectTags.vue';
    import TagCounterIndicator from '@/profile-creation-wizard/components/for-creation/ui/TagCounterIndicator.vue';
    import {pushVJoySnackbar} from '@maltjoy/core-vue';
    import TagSuggestion from '@/profile-creation-wizard/components/for-creation/ui/TagSuggestion.vue';
    import {Getter} from 'vuex-class';
    import getters from '@/profile-creation-wizard/store/creation/getters';
    import mutations from '@/profile-creation-wizard/store/creation/mutations';
    import BottomSheetDisplayer from '@/common/ui/BottomSheetDisplayer';
    import {differenceWith, isEqual} from 'lodash';
    import {ProfileCreationForm} from '@/profile-creation-wizard/types';
    import {I18nService} from '@/common/plugins/i18n';
    import CharacterCount from '@/profile-creation-wizard/components/for-creation/ui/1_headline/CharacterCount.vue';

    @Options({
        components: {
            CharacterCount,
            SelectedTags,
            InputSelectTags,
            TagCounterIndicator,
            TagSuggestion,
        },
    })
    export default class TagsSelection extends Vue {
        @Inject({from: DI.freelancerTagsService})
        freelancerTagsService!: FreelancerTagsService;
        @Getter(getters.creationForm)
        creationForm!: ProfileCreationForm;
        @Getter(getters.selectedCategories)
        selectedCategories!: Array<string>;

        MAX_SELECTED_TAGS_COUNT = 50;
        MAX_SUGGESTED_TAGS_COUNT = 7;

        bottomSheetSelector = '#details-skills-bottom-sheet';

        suggestedTags: Tag[] = [];

        async created(): Promise<void> {
            if (this.mainCategory) {
                const [data, error] = await this.freelancerTagsService.retrieveSuggestions(this.mainCategory);
                if (data && !error) {
                    this.suggestedTags = data.map((suggestedTag) => new Tag(suggestedTag.tag));
                }
            }
        }

        get tagsDifferrenceWith(): Tag[] {
            return differenceWith(this.suggestedTags, this.creationForm.tags, isEqual);
        }

        get isNotReachedLimit(): boolean {
            return this.creationForm.tags.length < this.MAX_SELECTED_TAGS_COUNT;
        }

        handlerAddTag(tag: Tag[]): void {
            if (this.isNotReachedLimit) {
                this.$store.commit(mutations.selectTag, tag);
            }
        }

        handlerRemoveTag(tag: Tag[]): void {
            this.$store.commit(mutations.unselectTag, tag);
        }

        get mainCategory(): string | null {
            if (!this.selectedCategories || !this.selectedCategories[0]) {
                return null;
            }
            return this.selectedCategories[0];
        }

        mounted(): void {
            this.bottomSheetDisplayer = new BottomSheetDisplayer(this.bottomSheetSelector, this.$el, 'div.profile-creation-wizard');
        }

        openBottomSheet(): void {
            this.bottomSheetDisplayer.openBottomSheet();
        }

        selectSearch(tag: Tag): void {
            if (!tag || !tag.name || tag.name.length == 0) {
                return;
            }
            if (tag.name.length > 100) {
                pushVJoySnackbar({
                    props: {
                        message: I18nService.msg('profile.creation.completion.wizard.details.skills.error.label.too.long', 100),
                        level: 'error',
                    },
                });
                return;
            }
            this.pushTag(tag);
        }

        pushTag(tag: Tag): void {
            if (!this.isNotReachedLimit) {
                pushVJoySnackbar({
                    props: {
                        message: this.$msg('profile.creation.completion.wizard.details.skills.error.max'),
                        level: 'error',
                    },
                });

                return;
            }

            const newTag = new Tag(tag.name);
            if (this.creationForm.tags.some((s: Tag) => s.sanitize() === newTag.sanitize())) {
                pushVJoySnackbar({
                    props: {
                        message: this.$msg('profile.creation.completion.wizard.details.skills.error.duplicate'),
                        level: 'error',
                    },
                });
                return;
            }
            this.$store.commit(mutations.selectTag, tag);
        }
    }
